/**
 * GuidedTemplateBuilder — 3-step wizard for structured template creation
 * 
 * Step 1: Template Identity (focus + variant → auto-name)
 * Step 2: Structure Review (editable slots with roles + families)  
 * Step 3: Exercise Selection (per-slot filtered picker + auto-fill)
 * 
 * Output: Pre-populated exercise list → handed to FullScreenWorkoutEditor
 */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Plus, Trash2, GripVertical, Search, X, Check, Shuffle, Save, ArrowUp, ArrowDown } from 'lucide-react';
import { searchExercises } from '../../utils/exerciseSearch';
import { getExerciseAnimationUrl } from '../../utils/exerciseImages';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// =============================================
// STRUCTURE TEMPLATES — Static slot definitions per focus
// =============================================
const STRUCTURE_TEMPLATES = {
  push: [
    { role: 'main',      slot_label: 'bench_press',      defaultSets: 4, defaultReps: 6,  defaultRest: 180 },
    { role: 'secondary', slot_label: 'overhead_press',    defaultSets: 4, defaultReps: 10, defaultRest: 120 },
    { role: 'accessory', slot_label: 'lateral_raise',     defaultSets: 3, defaultReps: 15, defaultRest: 90  },
    { role: 'accessory', slot_label: 'chest_fly',         defaultSets: 3, defaultReps: 12, defaultRest: 90  },
    { role: 'isolation', slot_label: 'tricep_extension',  defaultSets: 3, defaultReps: 15, defaultRest: 60  },
  ],
  pull: [
    { role: 'main',      slot_label: 'pulldown_pullup',  defaultSets: 4, defaultReps: 6,  defaultRest: 180 },
    { role: 'secondary', slot_label: 'row',               defaultSets: 4, defaultReps: 10, defaultRest: 120 },
    { role: 'accessory', slot_label: 'rear_delt',         defaultSets: 3, defaultReps: 15, defaultRest: 90  },
    { role: 'accessory', slot_label: 'curl',              defaultSets: 3, defaultReps: 12, defaultRest: 90  },
    { role: 'isolation', slot_label: 'rear_delt',         defaultSets: 3, defaultReps: 15, defaultRest: 60  },
  ],
  upper: [
    { role: 'main',      slot_label: 'bench_press',      defaultSets: 4, defaultReps: 6,  defaultRest: 180 },
    { role: 'secondary', slot_label: 'row',               defaultSets: 4, defaultReps: 8,  defaultRest: 120 },
    { role: 'accessory', slot_label: 'overhead_press',    defaultSets: 3, defaultReps: 12, defaultRest: 90  },
    { role: 'accessory', slot_label: 'lateral_raise',     defaultSets: 3, defaultReps: 15, defaultRest: 90  },
    { role: 'isolation', slot_label: 'curl',              defaultSets: 3, defaultReps: 12, defaultRest: 60  },
    { role: 'isolation', slot_label: 'tricep_extension',  defaultSets: 3, defaultReps: 12, defaultRest: 60  },
  ],
  lower: [
    { role: 'main',      slot_label: 'squat',            defaultSets: 4, defaultReps: 6,  defaultRest: 180 },
    { role: 'secondary', slot_label: 'deadlift',          defaultSets: 4, defaultReps: 8,  defaultRest: 120 },
    { role: 'accessory', slot_label: 'lunge',             defaultSets: 3, defaultReps: 12, defaultRest: 90  },
    { role: 'accessory', slot_label: 'leg_curl',          defaultSets: 3, defaultReps: 15, defaultRest: 90  },
    { role: 'isolation', slot_label: 'calf_raise',        defaultSets: 4, defaultReps: 15, defaultRest: 60  },
  ],
  full: [
    { role: 'main',      slot_label: 'squat',            defaultSets: 4, defaultReps: 5,  defaultRest: 180 },
    { role: 'main',      slot_label: 'bench_press',      defaultSets: 4, defaultReps: 5,  defaultRest: 180 },
    { role: 'secondary', slot_label: 'row',               defaultSets: 4, defaultReps: 8,  defaultRest: 120 },
    { role: 'accessory', slot_label: 'deadlift',          defaultSets: 3, defaultReps: 10, defaultRest: 90  },
    { role: 'accessory', slot_label: 'lateral_raise',     defaultSets: 3, defaultReps: 15, defaultRest: 90  },
    { role: 'isolation', slot_label: 'core_anti_extension', defaultSets: 3, defaultReps: 15, defaultRest: 60 },
  ]
};

// Human-readable labels for slot families
const FAMILY_LABELS = {
  bench_press: 'Bench Press', overhead_press: 'Overhead Press', incline_press: 'Incline Press',
  row: 'Row', pulldown_pullup: 'Pull-up / Pulldown', squat: 'Squat',
  deadlift: 'Deadlift / RDL', hip_thrust: 'Hip Thrust', lunge: 'Lunge / Split Squat',
  lateral_raise: 'Lateral Raise', rear_delt: 'Rear Delt / Face Pull', chest_fly: 'Chest Fly',
  curl: 'Bicep Curl', tricep_extension: 'Tricep Extension', leg_curl: 'Leg Curl',
  leg_extension: 'Leg Extension', calf_raise: 'Calf Raise',
  core_anti_extension: 'Core / Plank', core_flexion: 'Crunch / Ab',
  core_rotation: 'Rotation / Pallof', carry: 'Carry / Farmer Walk',
};

const ALL_FAMILIES = Object.keys(FAMILY_LABELS);

// Role styling
const ROLE_STYLES = {
  main:      { color: 'text-white',      bg: 'bg-white/20',      border: 'border-white/30',      dot: 'bg-white' },
  secondary: { color: 'text-blue-300',   bg: 'bg-blue-500/20',   border: 'border-blue-500/30',   dot: 'bg-blue-400' },
  accessory: { color: 'text-gray-300',   bg: 'bg-gray-500/15',   border: 'border-gray-500/20',   dot: 'bg-gray-400' },
  isolation: { color: 'text-gray-400',   bg: 'bg-gray-500/10',   border: 'border-gray-500/15',   dot: 'bg-gray-500' },
};

// Name patterns for exercise matching (works without exercise_family field)
const NAME_PATTERNS = {
  bench_press:      ['bench press', 'chest press', 'press de banca', 'press banca'],
  overhead_press:   ['overhead press', 'shoulder press', 'ohp', 'press militar', 'military press'],
  incline_press:    ['incline', 'inclinado'],
  row:              ['row', 'remo', 'bent over'],
  pulldown_pullup:  ['pull-up', 'pulldown', 'chin-up', 'dominadas', 'jalon', 'lat pull'],
  squat:            ['squat', 'sentadilla', 'leg press', 'prensa'],
  deadlift:         ['deadlift', 'peso muerto', 'rdl', 'romanian'],
  hip_thrust:       ['hip thrust', 'glute bridge', 'empuje de cadera'],
  lunge:            ['lunge', 'split squat', 'step-up', 'zancada', 'bulgarian'],
  lateral_raise:    ['lateral raise', 'elevacion lateral', 'lateral'],
  rear_delt:        ['face pull', 'reverse fly', 'rear delt', 'deltoides posterior'],
  chest_fly:        ['fly', 'cable fly', 'pec deck', 'aperturas', 'flye'],
  curl:             ['curl', 'bicep'],
  tricep_extension: ['pushdown', 'tricep extension', 'skull crusher', 'tricep', 'press francés'],
  leg_curl:         ['leg curl', 'hamstring curl', 'curl femoral'],
  leg_extension:    ['leg extension', 'extension de pierna', 'extension pierna'],
  calf_raise:       ['calf raise', 'calf', 'pantorrilla', 'gemelos'],
  core_anti_extension: ['plank', 'dead bug', 'ab wheel', 'plancha'],
  core_flexion:     ['crunch', 'sit-up', 'cable crunch'],
  core_rotation:    ['russian twist', 'pallof', 'woodchop', 'giro ruso'],
  carry:            ['farmer', 'carry', 'acarreo', 'loaded carry']
};

// Default exercise names for auto-fill
const DEFAULT_EXERCISES = {
  bench_press: 'Barbell Bench Press',
  overhead_press: 'Barbell Overhead Press',
  incline_press: 'Incline Dumbbell Press',
  row: 'Barbell Bent-Over Row',
  pulldown_pullup: 'Pull-ups',
  squat: 'Barbell Back Squat',
  deadlift: 'Romanian Deadlift',
  hip_thrust: 'Barbell Hip Thrust',
  lunge: 'Walking Lunges',
  lateral_raise: 'Dumbbell Lateral Raise',
  rear_delt: 'Face Pull',
  chest_fly: 'Cable Fly',
  curl: 'Barbell Curl',
  tricep_extension: 'Cable Tricep Pushdown',
  leg_curl: 'Seated Leg Curl',
  leg_extension: 'Machine Leg Extension',
  calf_raise: 'Standing Calf Raise',
  core_anti_extension: 'Plank',
  core_flexion: 'Cable Crunch',
  core_rotation: 'Pallof Press',
  carry: 'Farmer Walk'
};

// Focus display config
const FOCUS_OPTIONS = [
  { value: 'push',  label: 'Push',       icon: '💪', desc: 'Chest, shoulders, triceps' },
  { value: 'pull',  label: 'Pull',       icon: '🔙', desc: 'Back, biceps, rear delts' },
  { value: 'upper', label: 'Upper',      icon: '🏋️', desc: 'Full upper body' },
  { value: 'lower', label: 'Lower',      icon: '🦵', desc: 'Quads, hams, glutes, calves' },
  { value: 'full',  label: 'Full Body',  icon: '⚡', desc: 'Compound + accessory mix' },
];

const VARIANT_OPTIONS = ['A', 'B', 'C'];

// =============================================
// HELPER: Find exercises matching a slot family
// =============================================
const findExercisesForFamily = (family, exerciseLibrary) => {
  // First try exercise_family field (if Phase 1A is done)
  const familyMatches = exerciseLibrary.filter(ex => ex.exercise_family === family);
  if (familyMatches.length > 0) return familyMatches;
  
  // Fallback: name pattern matching
  const patterns = NAME_PATTERNS[family] || [];
  if (patterns.length === 0) return [];
  
  return exerciseLibrary.filter(ex => {
    const name = (ex.name || '').toLowerCase();
    const nameEs = (ex.name_es || '').toLowerCase();
    return patterns.some(p => name.includes(p) || nameEs.includes(p));
  });
};

// Find a single default exercise for auto-fill
const findDefaultExercise = (family, exerciseLibrary) => {
  const defaultName = DEFAULT_EXERCISES[family];
  if (!defaultName) return null;
  
  const lower = defaultName.toLowerCase();
  return exerciseLibrary.find(ex => 
    (ex.name || '').toLowerCase().includes(lower) ||
    (ex.name_es || '').toLowerCase().includes(lower)
  ) || findExercisesForFamily(family, exerciseLibrary)[0] || null;
};

// Pick a RANDOM exercise for a family — the engine behind varied auto-fill /
// re-rolls. Avoids the previous pick and (when possible) ids already used this
// roll so a template doesn't repeat the same exercise across slots.
const pickRandomForFamily = (family, exerciseLibrary, avoidId, usedIds) => {
  const pool = findExercisesForFamily(family, exerciseLibrary);
  if (!pool.length) return null;
  let candidates = pool;
  if (usedIds && usedIds.size) {
    const notUsed = candidates.filter(e => !usedIds.has(e.id));
    if (notUsed.length) candidates = notUsed;
  }
  if (avoidId && candidates.length > 1) {
    const notAvoid = candidates.filter(e => e.id !== avoidId);
    if (notAvoid.length) candidates = notAvoid;
  }
  return candidates[Math.floor(Math.random() * candidates.length)];
};

// Built-in structures exposed as quick-load presets in the builder.
const BUILT_IN_PRESETS = Object.entries(STRUCTURE_TEMPLATES).map(([focus, slots]) => ({
  id: `builtin-${focus}`,
  name: (FOCUS_OPTIONS.find(f => f.value === focus)?.label || focus),
  focus,
  slots,
  builtin: true,
}));

// Generate auto-name from focus + variant
const generateTemplateName = (focus, variant) => {
  const focusLabel = FOCUS_OPTIONS.find(f => f.value === focus)?.label || focus;
  return `${focusLabel} ${variant}`;
};

// =============================================
// MINI EXERCISE PICKER — Inline per-slot
// =============================================
const SlotExercisePicker = ({ family, exercises, onSelect, onClose }) => {
  const [search, setSearch] = useState('');
  
  const familyExercises = useMemo(() => findExercisesForFamily(family, exercises), [family, exercises]);
  
  const filtered = useMemo(() => {
    if (!search.trim()) return familyExercises.slice(0, 20);
    return searchExercises(search, exercises, 'es').slice(0, 20);
  }, [search, familyExercises, exercises]);
  
  return (
    <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-gray-900 border border-blue-500/30 rounded-xl shadow-2xl max-h-80 overflow-hidden" data-testid="slot-exercise-picker">
      <div className="p-2 border-b border-gray-700/50 flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={`Search ${FAMILY_LABELS[family] || family}...`}
            className="w-full pl-8 pr-3 py-1.5 bg-gray-800 border border-gray-600 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
            autoFocus
            data-testid="slot-picker-search"
          />
        </div>
        <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-white rounded-lg hover:bg-gray-700">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="overflow-y-auto max-h-60 p-1.5">
        {filtered.length === 0 ? (
          <p className="text-gray-500 text-sm text-center py-4">No exercises found</p>
        ) : (
          <div className="space-y-0.5">
            {filtered.map(ex => (
              <button
                key={ex.id}
                onClick={() => onSelect(ex)}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-blue-900/40 text-left transition-colors group"
                data-testid={`slot-pick-${ex.id}`}
              >
                {(ex.animation_webp || ex.image_url) && (
                  <img src={getExerciseAnimationUrl(ex)} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" loading="lazy" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm font-medium truncate group-hover:text-blue-200">{ex.name}</div>
                  {ex.name_es && ex.name_es !== ex.name && (
                    <div className="text-gray-500 text-xs truncate">{ex.name_es}</div>
                  )}
                </div>
                <Check className="w-4 h-4 text-blue-400 opacity-0 group-hover:opacity-100 flex-shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================
// MAIN COMPONENT
// =============================================
const GuidedTemplateBuilder = ({ exercises = [], onComplete, onSkip, onCancel }) => {
  const [step, setStep] = useState(1);
  
  // Step 1 state
  const [focus, setFocus] = useState(null);
  const [variant, setVariant] = useState('A');
  const [templateName, setTemplateName] = useState('');
  
  // Step 2 state — slots derived from focus selection
  const [slots, setSlots] = useState([]);
  
  // Step 3 state — selected exercises per slot index
  const [selectedExercises, setSelectedExercises] = useState({});
  const [activePickerSlot, setActivePickerSlot] = useState(null);

  // Saved structure presets (per trainer, from DB) + built-in quick structures.
  const [savedPresets, setSavedPresets] = useState([]);
  const [savingPreset, setSavingPreset] = useState(false);
  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchPresets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/trainer/structure-presets`, authHeaders());
      setSavedPresets(res.data || []);
    } catch (e) { /* non-blocking */ }
  }, []);
  useEffect(() => { fetchPresets(); }, [fetchPresets]);

  const handleLoadPreset = (preset) => {
    if (!preset) return;
    setSlots((preset.slots || []).map(s => ({ ...s })));
    setSelectedExercises({});
  };

  const handleSavePreset = async () => {
    if (!slots.length) return;
    const name = window.prompt('Name this structure preset:', templateName || (focus ? `${focus} structure` : 'My structure'));
    if (!name || !name.trim()) return;
    setSavingPreset(true);
    try {
      const res = await axios.post(`${API}/trainer/structure-presets`, { name: name.trim(), focus, slots }, authHeaders());
      setSavedPresets(prev => [res.data, ...prev]);
    } catch (e) {
      alert('Could not save preset');
    } finally {
      setSavingPreset(false);
    }
  };

  const handleDeletePreset = async (id) => {
    try {
      await axios.delete(`${API}/trainer/structure-presets/${id}`, authHeaders());
      setSavedPresets(prev => prev.filter(p => p.id !== id));
    } catch (e) { /* ignore */ }
  };
  
  // Initialize slots when focus changes
  const initializeSlots = useCallback((focusValue) => {
    const template = STRUCTURE_TEMPLATES[focusValue] || [];
    setSlots(template.map(s => ({ ...s })));
    setSelectedExercises({});
  }, []);
  
  // ========== STEP 1 HANDLERS ==========
  const handleFocusSelect = (f) => {
    setFocus(f);
    const name = generateTemplateName(f, variant);
    setTemplateName(name);
    initializeSlots(f);
  };
  
  const handleVariantSelect = (v) => {
    setVariant(v);
    if (focus) {
      setTemplateName(generateTemplateName(focus, v));
    }
  };
  
  const canProceedStep1 = focus !== null;
  
  // ========== STEP 2 HANDLERS ==========
  const updateSlot = (index, field, value) => {
    setSlots(prev => prev.map((s, i) => i === index ? { ...s, [field]: value } : s));
  };
  
  const addSlot = () => {
    setSlots(prev => [...prev, { role: 'accessory', slot_label: 'lateral_raise', defaultSets: 3, defaultReps: 12, defaultRest: 90 }]);
  };
  
  const removeSlot = (index) => {
    setSlots(prev => prev.filter((_, i) => i !== index));
    setSelectedExercises(prev => {
      const next = {};
      Object.entries(prev).forEach(([k, v]) => {
        const ki = parseInt(k);
        if (ki < index) next[ki] = v;
        else if (ki > index) next[ki - 1] = v;
      });
      return next;
    });
  };
  
  const moveSlot = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= slots.length) return;
    setSlots(prev => {
      const next = [...prev];
      [next[index], next[newIndex]] = [next[newIndex], next[index]];
      return next;
    });
    setSelectedExercises(prev => {
      const next = { ...prev };
      const a = next[index];
      const b = next[newIndex];
      if (a) next[newIndex] = a; else delete next[newIndex];
      if (b) next[index] = b; else delete next[index];
      return next;
    });
  };
  
  // ========== STEP 3 HANDLERS ==========
  const handleSelectExercise = (slotIndex, exercise) => {
    setSelectedExercises(prev => ({ ...prev, [slotIndex]: exercise }));
    setActivePickerSlot(null);
  };
  
  // Fill ONLY the empty slots with fresh random picks (keeps existing choices).
  // Clear a slot's exercise (or all) and click to swap just those.
  const handleFillEmpty = () => {
    const used = new Set(Object.values(selectedExercises).map(e => e?.id).filter(Boolean));
    const next = { ...selectedExercises };
    slots.forEach((slot, i) => {
      if (next[i]) return; // keep filled slots untouched
      const ex = pickRandomForFamily(slot.slot_label, exercises, null, used);
      if (ex) { next[i] = ex; used.add(ex.id); }
    });
    setSelectedExercises(next);
  };

  // Re-roll a single slot to a different random exercise from its family.
  const handleRerollSlot = (index) => {
    const used = new Set(Object.values(selectedExercises).map(e => e?.id).filter(Boolean));
    const prevId = selectedExercises[index]?.id;
    const ex = pickRandomForFamily(slots[index].slot_label, exercises, prevId, used);
    if (ex) setSelectedExercises(prev => ({ ...prev, [index]: ex }));
  };
  
  const filledCount = Object.keys(selectedExercises).length;
  const allFilled = filledCount === slots.length;
  
  // ========== FINALIZE ==========
  const handleComplete = () => {
    const exerciseList = slots.map((slot, i) => {
      const ex = selectedExercises[i];
      if (!ex) return null;
      return {
        exercise_id: ex.id,
        exercise_name: ex.name,
        target_sets: slot.defaultSets,
        target_reps: slot.defaultReps,
        rest_time: slot.defaultRest,
        role: slot.role,
        slot_label: slot.slot_label,
        is_superset: false,
        superset_group: null,
        actual_sets: [],
        note: '',
        image_url: ex.image_url || '',
        animation_webp: ex.animation_webp || '',
        muscle_groups: ex.muscle_groups || [],
        difficulty: ex.difficulty || '',
        equipment: ex.equipment || '',
      };
    }).filter(Boolean);
    
    onComplete({
      name: templateName.trim() || generateTemplateName(focus, variant),
      exercises: exerciseList,
      tags: [focus, variant.toLowerCase()],
      focus: focus,
    });
  };
  
  // =============================================
  // RENDER
  // =============================================
  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-4" style={{ zIndex: 99999 }} data-testid="guided-template-builder">
      <div className="bg-gray-900 border border-gray-700/50 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700/50">
          <div>
            <h2 className="text-xl font-bold text-white">Create Template</h2>
            <div className="flex items-center gap-3 mt-1">
              {[1, 2, 3].map(s => (
                <div key={s} className="flex items-center gap-1.5">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    step === s ? 'bg-blue-500 text-white' : step > s ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-400'
                  }`}>{step > s ? '✓' : s}</div>
                  <span className={`text-xs ${step === s ? 'text-blue-300' : 'text-gray-500'}`}>
                    {s === 1 ? 'Focus' : s === 2 ? 'Structure' : 'Exercises'}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-white p-2 rounded-lg hover:bg-gray-800" data-testid="builder-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          
          {/* ========== STEP 1: Focus & Variant ========== */}
          {step === 1 && (
            <div className="space-y-6" data-testid="builder-step-1">
              <div>
                <label className="text-sm font-semibold text-gray-300 mb-3 block">Focus</label>
                <div className="grid grid-cols-5 gap-2">
                  {FOCUS_OPTIONS.map(f => (
                    <button
                      key={f.value}
                      onClick={() => handleFocusSelect(f.value)}
                      className={`p-3 rounded-xl text-center transition-all border-2 ${
                        focus === f.value
                          ? 'bg-blue-600/30 border-blue-400 text-white shadow-lg shadow-blue-500/20'
                          : 'bg-gray-800/50 border-transparent text-gray-300 hover:bg-gray-700/50 hover:border-gray-600'
                      }`}
                      data-testid={`focus-${f.value}`}
                    >
                      <div className="text-2xl mb-1">{f.icon}</div>
                      <div className="font-semibold text-sm">{f.label}</div>
                      <div className="text-xs opacity-60 mt-0.5">{f.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="text-sm font-semibold text-gray-300 mb-3 block">Variant</label>
                <div className="flex gap-2">
                  {VARIANT_OPTIONS.map(v => (
                    <button
                      key={v}
                      onClick={() => handleVariantSelect(v)}
                      className={`w-14 h-14 rounded-xl font-bold text-lg transition-all border-2 ${
                        variant === v
                          ? 'bg-blue-600/30 border-blue-400 text-white'
                          : 'bg-gray-800/50 border-transparent text-gray-400 hover:bg-gray-700/50 hover:border-gray-600'
                      }`}
                      data-testid={`variant-${v}`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="text-sm font-semibold text-gray-300 mb-2 block">Template Name</label>
                <input
                  type="text"
                  value={templateName}
                  onChange={e => setTemplateName(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 text-lg"
                  placeholder="e.g. Upper Push A"
                  data-testid="template-name-input"
                />
              </div>
            </div>
          )}
          
          {/* ========== STEP 2: Structure ========== */}
          {step === 2 && (
            <div className="space-y-3" data-testid="builder-step-2">
              <div className="flex items-center justify-between gap-2 mb-1">
                <p className="text-gray-400 text-sm">Review the structure. Reorder, edit roles and families.</p>
                <button
                  onClick={handleSavePreset}
                  disabled={savingPreset || !slots.length}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600/20 text-indigo-300 hover:bg-indigo-600/30 text-sm font-medium transition-colors disabled:opacity-40"
                  data-testid="save-preset-btn"
                >
                  <Save className="w-3.5 h-3.5" /> {savingPreset ? 'Saving…' : 'Save structure'}
                </button>
              </div>

              {/* Preset quick-load — built-in structures + your saved ones */}
              <div className="flex flex-wrap gap-1.5" data-testid="structure-preset-bar">
                <span className="text-gray-500 text-xs self-center mr-0.5">Load:</span>
                {BUILT_IN_PRESETS.map(p => (
                  <button
                    key={p.id}
                    onClick={() => handleLoadPreset(p)}
                    className="px-2.5 py-1 rounded-full bg-gray-800 border border-gray-600 text-xs text-gray-300 hover:text-white hover:border-blue-500/50 transition"
                    data-testid={`load-preset-${p.id}`}
                    title={`Built-in ${p.name} structure`}
                  >
                    {p.name}
                  </button>
                ))}
                {savedPresets.map(p => (
                  <span key={p.id} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full bg-indigo-900/40 border border-indigo-500/40 text-xs text-indigo-200">
                    <button onClick={() => handleLoadPreset(p)} className="hover:text-white" title="Load this saved structure" data-testid={`load-preset-${p.id}`}>{p.name}</button>
                    <button onClick={() => handleDeletePreset(p.id)} className="text-indigo-400 hover:text-red-400" title="Delete preset" data-testid={`delete-preset-${p.id}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              
              {slots.map((slot, i) => {
                const rs = ROLE_STYLES[slot.role] || ROLE_STYLES.accessory;
                return (
                  <div key={i} className={`flex items-center gap-2 p-3 rounded-lg border ${rs.border} ${rs.bg} transition-all`} data-testid={`slot-${i}`}>
                    <div className="flex flex-col gap-0.5">
                      <button onClick={() => moveSlot(i, -1)} disabled={i === 0} className="text-gray-500 hover:text-white disabled:opacity-20 text-xs">▲</button>
                      <button onClick={() => moveSlot(i, 1)} disabled={i === slots.length - 1} className="text-gray-500 hover:text-white disabled:opacity-20 text-xs">▼</button>
                    </div>
                    <span className="text-gray-500 text-sm font-mono w-5">{i + 1}</span>
                    <select
                      value={slot.role}
                      onChange={e => updateSlot(i, 'role', e.target.value)}
                      className={`px-2 py-1 rounded-lg text-xs font-bold uppercase bg-transparent border ${rs.border} ${rs.color} focus:outline-none focus:border-blue-500 cursor-pointer`}
                      data-testid={`slot-role-${i}`}
                    >
                      {['main', 'secondary', 'accessory', 'isolation'].map(r => (
                        <option key={r} value={r} className="bg-gray-800 text-white">{r}</option>
                      ))}
                    </select>
                    <select
                      value={slot.slot_label}
                      onChange={e => updateSlot(i, 'slot_label', e.target.value)}
                      className="flex-1 px-2 py-1 rounded-lg text-sm bg-gray-800/50 border border-gray-600/50 text-gray-200 focus:outline-none focus:border-blue-500 cursor-pointer"
                      data-testid={`slot-family-${i}`}
                    >
                      {ALL_FAMILIES.map(f => (
                        <option key={f} value={f} className="bg-gray-800">{FAMILY_LABELS[f]}</option>
                      ))}
                    </select>
                    <input type="number" value={slot.defaultSets} onChange={e => updateSlot(i, 'defaultSets', parseInt(e.target.value) || 1)} min="1" max="10" className="w-12 px-1 py-1 rounded text-center text-sm bg-gray-800/50 border border-gray-600/50 text-white focus:outline-none focus:border-blue-500" title="Sets" />
                    <span className="text-gray-600 text-xs">x</span>
                    <input type="number" value={slot.defaultReps} onChange={e => updateSlot(i, 'defaultReps', parseInt(e.target.value) || 1)} min="1" max="100" className="w-12 px-1 py-1 rounded text-center text-sm bg-gray-800/50 border border-gray-600/50 text-white focus:outline-none focus:border-blue-500" title="Reps" />
                    <span className="text-gray-600 text-xs">{slot.defaultRest}s</span>
                    <button onClick={() => removeSlot(i)} className="text-gray-500 hover:text-red-400 p-1 rounded hover:bg-red-500/10" data-testid={`remove-slot-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
              
              <button onClick={addSlot} className="w-full py-2 border-2 border-dashed border-gray-700 rounded-lg text-gray-500 hover:text-blue-300 hover:border-blue-500/50 text-sm transition-colors flex items-center justify-center gap-1" data-testid="add-slot-btn">
                <Plus className="w-4 h-4" /> Add Slot
              </button>
            </div>
          )}
          
          {/* ========== STEP 3: Exercise Selection ========== */}
          {step === 3 && (
            <div className="space-y-2" data-testid="builder-step-3">
              <div className="flex items-center justify-between mb-4 gap-2">
                <p className="text-gray-400 text-sm">Pick an exercise per slot. Clear a slot (✕) or roll a single one (🎲), then fill the empties.</p>
                <button
                  onClick={handleFillEmpty}
                  disabled={slots.every((_, i) => selectedExercises[i])}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-fuchsia-600/20 text-fuchsia-300 hover:bg-fuchsia-600/30 text-sm font-medium transition-colors flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="roll-again-btn"
                  title="Fill only the empty slots with fresh random exercises (keeps your current picks)"
                >
                  <Shuffle className="w-3.5 h-3.5" /> Fill empty slots
                </button>
              </div>
              
              {slots.map((slot, i) => {
                const rs = ROLE_STYLES[slot.role] || ROLE_STYLES.accessory;
                const selected = selectedExercises[i];
                const isPickerOpen = activePickerSlot === i;
                
                return (
                  <div key={i} className="relative" data-testid={`exercise-slot-${i}`}>
                    <div className={`flex items-center gap-3 p-3 rounded-lg border ${selected ? 'border-green-500/40 bg-green-900/10' : `${rs.border} ${rs.bg}`} transition-all`}>
                      <div className="flex flex-col flex-shrink-0">
                        <button
                          onClick={() => moveSlot(i, -1)}
                          disabled={i === 0}
                          className="text-gray-500 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed"
                          title="Move up"
                          data-testid={`slot-up-${i}`}
                        >
                          <ArrowUp className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => moveSlot(i, 1)}
                          disabled={i === slots.length - 1}
                          className="text-gray-500 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed"
                          title="Move down"
                          data-testid={`slot-down-${i}`}
                        >
                          <ArrowDown className="w-3 h-3" />
                        </button>
                      </div>
                      <span className="text-gray-500 text-sm font-mono w-5">{i + 1}</span>
                      <div className={`w-2 h-2 rounded-full ${rs.dot} flex-shrink-0`} />
                      <span className={`text-xs font-bold uppercase tracking-wide w-20 ${rs.color}`}>{slot.role}</span>
                      <span className="text-gray-500 text-xs w-28 truncate">{FAMILY_LABELS[slot.slot_label] || slot.slot_label}</span>
                      
                      {selected ? (
                        <div className="flex-1 flex items-center gap-2 min-w-0">
                          {(selected.animation_webp || selected.image_url) && (
                            <img src={getExerciseAnimationUrl(selected)} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />
                          )}
                          <span className="text-white text-sm font-medium truncate">{selected.name}</span>
                          <span className="text-green-400 text-xs flex-shrink-0">{slot.defaultSets}x{slot.defaultReps}</span>
                          <button
                            onClick={() => handleRerollSlot(i)}
                            className="text-gray-400 hover:text-fuchsia-300 p-0.5 rounded flex-shrink-0 ml-auto"
                            title="Roll a different exercise for this slot"
                            data-testid={`reroll-slot-${i}`}
                          >
                            <Shuffle className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => { setSelectedExercises(prev => { const next = { ...prev }; delete next[i]; return next; }); }}
                            className="text-gray-400 hover:text-red-400 p-0.5 rounded flex-shrink-0"
                            title="Remove"
                            data-testid={`clear-slot-${i}`}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setActivePickerSlot(isPickerOpen ? null : i)}
                          className="flex-1 text-left text-blue-400 hover:text-blue-300 text-sm py-1 transition-colors"
                          data-testid={`select-exercise-${i}`}
                        >
                          {isPickerOpen ? 'Cancel' : 'Select exercise...'}
                        </button>
                      )}
                    </div>
                    
                    {/* Inline picker */}
                    {isPickerOpen && (
                      <SlotExercisePicker
                        family={slot.slot_label}
                        exercises={exercises}
                        onSelect={(ex) => handleSelectExercise(i, ex)}
                        onClose={() => setActivePickerSlot(null)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-700/50">
          <div>
            {step === 1 && (
              <button onClick={onSkip} className="text-gray-400 hover:text-white text-sm transition-colors" data-testid="skip-builder">
                Skip — Start Blank
              </button>
            )}
            {step > 1 && (
              <button onClick={() => setStep(step - 1)} className="flex items-center gap-1 text-gray-300 hover:text-white text-sm transition-colors" data-testid="builder-back">
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            {step < 3 ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={step === 1 && !canProceedStep1}
                className="flex items-center gap-1 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-medium text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="builder-next"
              >
                Continue <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleComplete}
                disabled={filledCount === 0}
                className="flex items-center gap-1 px-5 py-2.5 bg-green-600 hover:bg-green-500 text-white rounded-xl font-medium text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="builder-complete"
              >
                <Check className="w-4 h-4" /> Open in Editor ({filledCount}/{slots.length})
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default GuidedTemplateBuilder;
